// What the shop owes its suppliers (docs/plans/supplier-bills.md): the bill part of a
// delivery, paying a supplier, sending goods back, and the lists built on them.
// Build every write with core `supplier-bills.ts`; what is owed is summed, never stored.
import {
  buildSupplierBill,
  buildSupplierCredit,
  buildSupplierPayment,
  creditLineAmount,
  spreadPayment,
  voidSupplierBill,
  voidSupplierCredit,
  voidSupplierPayment,
  type SyncOp,
} from '@ims/core'
import type {
  PaymentMethod,
  SupplierBillSummary,
  SupplierCreditSummary,
  SupplierPaymentSummary,
  TableName,
} from '@ims/data'
import {
  arrivalClass,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  FilterPill,
  FilterPills,
  IconTile,
  Input,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  SkeletonTable,
  Stat,
  Switch,
  TD,
  TH,
  TR,
  Table,
  cx,
  useToast,
  Pagination,
  Kbd,
  ListFrame,
  ListToolbar,
  RowActions,
  RowNote,
  RowTitle,
  useArrivals,
} from '@ims/ui'
import { AlertTriangle, HandCoins, Package, Plus, Receipt, Undo2 } from '@ims/ui/icons'
import { useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { DateInput, MoneyInput, QtyInput, useLookups } from '../components.tsx'
import { usePaged } from '../lib/paging.ts'
import { useListKeys } from '../lib/list-keys.ts'
import { moveClick, useOpen } from '../lib/motion.ts'
import { useLive, useSettings } from '../hooks.ts'
import { NewPartyDialog } from '../party-picker.tsx'
import { dateLabel, dateShort, money, qty, todayIso } from '../lib/format.ts'
import {
  PAY_METHODS,
  addDays,
  billOwed,
  billStatus,
  dueIn,
  payMethodLabel,
  supplierBillRow,
  supplierCreditRow,
  supplierPaymentRow,
} from '../lib/payables.ts'
import { calendarOf } from '../lib/reports.ts'
import { useCan } from '../workspace.tsx'
import { useBuildContext, useWrite } from './order-hooks.ts'
import { SectionTabs } from '../layout/sections.tsx'

/** Tables a screen that shows what is owed must re-read on. */
export const PAYABLE_TABLES: TableName[] = [
  'supplier_bills',
  'supplier_payments',
  'supplier_credits',
  'supplier_allocations',
]

const RETURN_REASONS = ['Damaged', 'Wrong item', 'Expired', 'Too many sent', 'Price corrected']

/** The live bills of some receipts, by receipt id. */
export function useReceiptBills(receiptIds: readonly string[]) {
  const key = receiptIds.join(',')
  const bills = useLive(
    (r) => r.payables?.bills({ receiptIds: key ? key.split(',') : [] }) ?? Promise.resolve([]),
    [key],
    PAYABLE_TABLES,
  ).data
  return new Map((bills ?? []).map((b) => [b.bill.goods_receipt_id!, b]))
}

export function BillBadge({ bill, today }: { bill: SupplierBillSummary; today: string }) {
  const status = billStatus(bill, today)
  return <Badge tone={status.tone}>{status.label}</Badge>
}

// -----------------------------------------------------------------------------
// The bill part of a form (receiving stock, adding a bill)
// -----------------------------------------------------------------------------

export interface BillDraft {
  billDate: string
  dueDate: string
  /** Empty = no bill is recorded. */
  totalPaisa: number | null
  paidPaisa: number | null
  method: PaymentMethod
}

export const emptyBillDraft = (): BillDraft => ({
  billDate: todayIso(),
  dueDate: '',
  totalPaisa: null,
  paidPaisa: null,
  method: 'cash',
})

const DUE_CHOICES = [
  { label: 'Today', days: 0 },
  { label: '7 days', days: 7 },
  { label: '15 days', days: 15 },
  { label: '30 days', days: 30 },
]

/** What a bill draft would write: the bill, and a payment for what was paid now. */
export function billDraftProblem(draft: BillDraft): string | null {
  if (!draft.totalPaisa) return null
  if ((draft.paidPaisa ?? 0) > draft.totalPaisa) return 'Paid now is more than the bill.'
  if (draft.dueDate && draft.dueDate < draft.billDate) return 'The due date is before the bill.'
  return null
}

export function BillFields({
  draft,
  onChange,
  totalHint,
}: {
  draft: BillDraft
  onChange: (patch: Partial<BillDraft>) => void
  /** Under the total, e.g. where the number came from. */
  totalHint?: string
}) {
  const settings = useSettings()
  const cal = calendarOf(settings)
  const total = draft.totalPaisa ?? 0
  const left = Math.max(0, total - (draft.paidPaisa ?? 0))
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <DateInput
        label="Bill date"
        calendar={cal}
        value={draft.billDate}
        onChange={(billDate) => onChange({ billDate })}
      />
      <div>
        {draft.dueDate ? (
          <DateInput
            label="Due"
            calendar={cal}
            value={draft.dueDate}
            onChange={(dueDate) => onChange({ dueDate })}
          />
        ) : (
          // A date picker cannot be empty, so no due date is its own state.
          <div>
            <span className="mb-1 block text-sm font-medium text-muted">Due</span>
            <button
              type="button"
              onClick={() => onChange({ dueDate: addDays(draft.billDate, 15) })}
              className="flex h-10 w-full items-center rounded-xl border border-dashed border-border px-3 text-left text-sm text-muted hover:border-primary/40 hover:text-ink"
            >
              No due date. Set one
            </button>
          </div>
        )}
        <div className="mt-1.5 flex flex-wrap gap-1">
          {DUE_CHOICES.map((c) => {
            const date = addDays(draft.billDate, c.days)
            const on = draft.dueDate === date
            return (
              <button
                key={c.label}
                type="button"
                aria-pressed={on}
                onClick={() => onChange({ dueDate: on ? '' : date })}
                className={cx(
                  'rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                  on
                    ? 'border-primary/40 bg-primary-light text-primary'
                    : 'border-border text-muted hover:text-ink',
                )}
              >
                {c.label}
              </button>
            )
          })}
        </div>
      </div>
      <Field label="Bill total" hint={totalHint}>
        {(id) => (
          <MoneyInput
            id={id}
            paisa={draft.totalPaisa}
            onValue={(totalPaisa) => onChange({ totalPaisa })}
            prefix="Rs."
          />
        )}
      </Field>
      <div className="grid grid-cols-[minmax(0,1fr)_7.5rem] gap-2">
        <Field label="Paid now">
          {(id) => (
            <MoneyInput
              id={id}
              paisa={draft.paidPaisa}
              disabled={!total}
              onValue={(paidPaisa) => onChange({ paidPaisa })}
              prefix="Rs."
            />
          )}
        </Field>
        <Field label="How">
          {(id) => (
            <Select
              id={id}
              value={draft.method}
              disabled={!draft.paidPaisa}
              onChange={(e) => onChange({ method: e.target.value as PaymentMethod })}
            >
              {PAY_METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {total > 0 && (
        <div
          className={cx(
            'flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm font-semibold sm:col-span-2',
            left > 0 ? 'bg-warning-light text-warning' : 'bg-success-light text-success',
          )}
        >
          <span>{left > 0 ? 'Still to pay' : 'Paid in full'}</span>
          <span className="flex items-center gap-2">
            {left > 0 && (
              <button
                type="button"
                onClick={() => onChange({ paidPaisa: total })}
                className="rounded-lg px-2 py-0.5 text-xs underline"
              >
                Paid it all
              </button>
            )}
            <span className="tabular text-base">{money(left, settings)}</span>
          </span>
        </div>
      )}
    </div>
  )
}

/** The ops of a bill draft: the bill, and the payment made now set against it. */
export function billDraftOps(
  ctx: { tenantId: string; now: string },
  draft: BillDraft,
  bill: {
    supplierId: string
    goodsReceiptId?: string | null
    billNo?: string | null
    notes?: string | null
  },
): SyncOp[] {
  if (!draft.totalPaisa) return []
  const built = buildSupplierBill(ctx, {
    ...bill,
    billDate: draft.billDate,
    dueDate: draft.dueDate || null,
    totalPaisa: draft.totalPaisa,
  })
  const paid = draft.paidPaisa ?? 0
  if (paid <= 0) return built.ops
  const payment = buildSupplierPayment(ctx, {
    supplierId: bill.supplierId,
    amountPaisa: paid,
    method: draft.method,
    paymentDate: draft.billDate > todayIso() ? draft.billDate : todayIso(),
    notes: bill.goodsReceiptId ? 'Paid when the stock came' : null,
    allocations: [{ billId: built.bill.id, amountPaisa: Math.min(paid, draft.totalPaisa) }],
  })
  return [...built.ops, ...payment.ops]
}

// -----------------------------------------------------------------------------
// Add a bill: for a delivery that has none, or one with no delivery at all
// -----------------------------------------------------------------------------

export function AddBillDialog({
  supplierId,
  supplierName,
  receipt,
  onClose,
}: {
  supplierId: string
  supplierName: string
  /** The delivery it belongs to, with what its lines add up to. */
  receipt?: { id: string; reference: string | null; totalPaisa: number; date: string }
  onClose: () => void
}) {
  const context = useBuildContext()
  const { write, busy } = useWrite()
  const toast = useToast()
  const [billNo, setBillNo] = useState(receipt?.reference ?? '')
  const [note, setNote] = useState('')
  const [draft, setDraft] = useState<BillDraft>(() => ({
    ...emptyBillDraft(),
    billDate: receipt?.date ?? todayIso(),
    totalPaisa: receipt?.totalPaisa || null,
  }))
  const problem = billDraftProblem(draft)
  const save = async () => {
    const ok = await write(() =>
      billDraftOps(context(), draft, {
        supplierId,
        goodsReceiptId: receipt?.id ?? null,
        billNo,
        notes: note,
      }),
    )
    if (ok) {
      toast.success('Bill added')
      onClose()
    }
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={receipt ? `Add the bill from ${supplierName}` : `Add a bill from ${supplierName}`}
      size="lg"
      footer={
        <>
          {problem && <span className="mr-auto self-center text-sm text-danger">{problem}</span>}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!draft.totalPaisa || !!problem}
            onClick={() => void save()}
          >
            Add bill
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!receipt && (
          <p className="text-sm text-muted">
            For money you owe that did not come with a delivery: an old balance, transport, a
            repair. It does not change stock.
          </p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Bill no. (optional)">
            {(id) => <Input id={id} value={billNo} onChange={(e) => setBillNo(e.target.value)} />}
          </Field>
          <Field label={receipt ? 'Note (optional)' : 'What is it for?'}>
            {(id) => (
              <Input
                id={id}
                value={note}
                maxLength={200}
                placeholder={receipt ? undefined : 'e.g. Old balance'}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
        </div>
        <BillFields
          draft={draft}
          onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          totalHint={receipt ? 'From what was received. Change it if the bill differs.' : undefined}
        />
      </div>
    </Modal>
  )
}

// -----------------------------------------------------------------------------
// Pay a supplier
// -----------------------------------------------------------------------------

export function PaySupplierDialog({
  supplierId,
  supplierName,
  billId,
  onClose,
}: {
  supplierId: string
  supplierName: string
  /** Opened from one bill: that bill is paid first, and the amount starts at what it owes. */
  billId?: string
  onClose: () => void
}) {
  const settings = useSettings()
  const context = useBuildContext()
  const { write, busy } = useWrite()
  const toast = useToast()
  const today = todayIso()
  const bills = useLive(
    (r) => r.payables?.bills({ supplierId, open: true }) ?? Promise.resolve([]),
    [supplierId],
    PAYABLE_TABLES,
  ).data
  const [amount, setAmount] = useState<number | null>(null)
  const [touched, setTouched] = useState(false)
  const [date, setDate] = useState(today)
  const [method, setMethod] = useState<PaymentMethod>('cash')
  const [reference, setReference] = useState('')
  /** Shares typed by hand; a bill not in here takes what the spread gives it. */
  const [manual, setManual] = useState<Record<string, number> | null>(null)

  const open = bills ?? []
  const owedTotal = open.reduce((sum, b) => sum + billOwed(b), 0)
  const first = open.find((b) => b.bill.id === billId)
  const paying = touched ? (amount ?? 0) : first ? billOwed(first) : owedTotal
  // The bill it was opened from first, then the oldest due.
  const ordered = [...(first ? [first] : []), ...open.filter((b) => b !== first)]
  const spread = new Map(
    spreadPayment(
      paying,
      ordered.map((b, i) => ({
        id: b.bill.id,
        owedPaisa: billOwed(b),
        // Keep this order: the first bill sorts before every date.
        dueDate: first && i === 0 ? '0000-00-00' : (b.bill.due_date ?? b.bill.bill_date),
      })),
    ).map((s) => [s.billId, s.amountPaisa]),
  )
  const shareOf = (id: string) => (manual ? (manual[id] ?? 0) : (spread.get(id) ?? 0))
  const allocated = ordered.reduce((sum, b) => sum + shareOf(b.bill.id), 0)
  const over = ordered.some((b) => shareOf(b.bill.id) > billOwed(b))
  const advance = paying - allocated
  const problem =
    paying <= 0
      ? 'Enter the amount you paid.'
      : advance < 0
        ? 'More is set against bills than you are paying.'
        : over
          ? 'A bill is paid more than it owes.'
          : null

  const save = async () => {
    const ok = await write(
      () =>
        buildSupplierPayment(context(), {
          supplierId,
          amountPaisa: paying,
          method,
          paymentDate: date,
          reference,
          allocations: ordered
            .map((b) => ({ billId: b.bill.id, amountPaisa: shareOf(b.bill.id) }))
            .filter((a) => a.amountPaisa > 0),
        }).ops,
    )
    if (ok) {
      toast.success(`Paid ${money(paying, settings)} to ${supplierName}`)
      onClose()
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Pay ${supplierName}`}
      size="lg"
      footer={
        <>
          {problem && paying > 0 && (
            <span className="mr-auto self-center text-sm text-danger">{problem}</span>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!!problem || !bills} onClick={() => void save()}>
            Record payment
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          {!bills
            ? 'Loading their bills…'
            : open.length === 0
              ? 'Nothing is owed to them. A payment now is kept as an advance.'
              : `You owe ${money(owedTotal, settings)} on ${open.length} bill${open.length === 1 ? '' : 's'}.`}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Amount">
            {(id) => (
              <MoneyInput
                id={id}
                paisa={touched ? amount : paying}
                onValue={(v) => {
                  setTouched(true)
                  setAmount(v)
                  setManual(null)
                }}
                prefix="Rs."
              />
            )}
          </Field>
          <DateInput label="Date" calendar={calendarOf(settings)} value={date} onChange={setDate} />
          <Field label="How">
            {(id) => (
              <Select
                id={id}
                value={method}
                onChange={(e) => setMethod(e.target.value as PaymentMethod)}
              >
                {PAY_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Reference (optional)">
            {(id) => (
              <Input
                id={id}
                value={reference}
                maxLength={100}
                placeholder="Cheque or transfer no."
                onChange={(e) => setReference(e.target.value)}
              />
            )}
          </Field>
        </div>
        {ordered.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-ink">Set against</h3>
            <p className="mb-2 text-xs text-muted">
              Oldest bill first. Change a line to pay a different bill.
            </p>
            <div className="overflow-hidden rounded-xl border border-border">
              <Table stack>
                <thead>
                  <tr>
                    <TH>Bill</TH>
                    <TH>Due</TH>
                    <TH align="right">To pay</TH>
                    <TH align="right">Paying now</TH>
                    <TH />
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((b) => {
                    const name = b.bill.bill_no || b.bill.notes || 'Bill'
                    const share = shareOf(b.bill.id)
                    return (
                      <tr key={b.bill.id}>
                        <TD className="stack-main stack-r1 font-medium">{name}</TD>
                        <TD className="stack-r2 text-muted max-sm:text-xs">
                          {b.bill.due_date ? dateShort(b.bill.due_date, settings) : 'No due date'}{' '}
                          {billStatus(b, today).lateDays > 0 && (
                            <BillBadge bill={b} today={today} />
                          )}
                        </TD>
                        <TD align="right" className="stack-end stack-r1 tabular">
                          {money(billOwed(b), settings)}
                        </TD>
                        <TD align="right" className="stack-r3">
                          <div className="ml-auto w-32 max-sm:ml-0 max-sm:w-full">
                            <MoneyInput
                              aria-label={`Paying against ${name}`}
                              paisa={share || null}
                              onValue={(v) =>
                                setManual({
                                  ...Object.fromEntries(
                                    ordered.map((x) => [x.bill.id, shareOf(x.bill.id)]),
                                  ),
                                  [b.bill.id]: v ?? 0,
                                })
                              }
                            />
                          </div>
                        </TD>
                        <TD className="stack-end stack-r2 w-24">
                          {share >= billOwed(b) && share > 0 ? (
                            <Badge tone="success" className="whitespace-nowrap">
                              Paid in full
                            </Badge>
                          ) : share > 0 ? (
                            <Badge tone="warning">Part</Badge>
                          ) : null}
                        </TD>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl bg-soft px-3.5 py-2.5">
            <div className="text-xs text-muted">Paying</div>
            <div className="tabular text-lg font-bold text-ink">{money(paying, settings)}</div>
          </div>
          <div
            className={cx(
              'rounded-xl px-3.5 py-2.5',
              advance > 0 ? 'bg-info-light' : 'bg-warning-light text-warning',
            )}
          >
            <div className="text-xs">
              {advance > 0 ? 'Kept as an advance' : 'Still owed after this'}
            </div>
            <div className="tabular text-lg font-bold">
              {money(advance > 0 ? advance : Math.max(0, owedTotal - allocated), settings)}
            </div>
          </div>
        </div>
        <p className="text-xs text-muted">
          Paying more than you owe is fine: the extra is kept as an advance with this supplier.
        </p>
      </div>
    </Modal>
  )
}

// -----------------------------------------------------------------------------
// Return goods to a supplier (or take a price correction)
// -----------------------------------------------------------------------------

export function ReturnGoodsDialog({
  supplierId,
  supplierName,
  receiptId,
  onClose,
}: {
  supplierId: string
  supplierName: string
  receiptId?: string
  onClose: () => void
}) {
  const settings = useSettings()
  const lookups = useLookups()
  const context = useBuildContext()
  const { write, busy } = useWrite()
  const toast = useToast()
  const [picked, setPicked] = useState(receiptId ?? '')
  const [reason, setReason] = useState(RETURN_REASONS[0]!)
  const [creditNo, setCreditNo] = useState('')
  const [amountOnly, setAmountOnly] = useState(false)
  const [amount, setAmount] = useState<number | null>(null)
  const [back, setBack] = useState<Record<string, number>>({})
  const receipts = useLive(
    (r) => r.purchases.receipts({ supplierId, limit: 50 }),
    [supplierId],
    ['goods_receipts', 'goods_receipt_lines'],
  ).data?.rows
  const chosen = picked || receipts?.[0]?.receipt.id || ''
  const detail = useLive(
    async (r) => {
      if (!chosen) return null
      const d = await r.purchases.receiptDetail(chosen)
      if (!d) return null
      const products = await Promise.all(d.lines.map((l) => r.get('products', l.product_id)))
      return { ...d, products }
    },
    [chosen],
    ['goods_receipts', 'goods_receipt_lines', 'products'],
  ).data
  const bills = useLive(
    (r) => r.payables?.bills({ supplierId, open: true }) ?? Promise.resolve([]),
    [supplierId],
    PAYABLE_TABLES,
  ).data

  const lines = (detail?.lines ?? []).map((l, i) => ({
    line: l,
    product: detail!.products[i] ?? null,
    qtyMilli: Math.min(back[l.id] ?? 0, l.qty_milli),
  }))
  const goods = lines.filter((l) => l.qtyMilli > 0)
  const total = amountOnly
    ? (amount ?? 0)
    : goods.reduce(
        (sum, l) =>
          sum + creditLineAmount({ qtyMilli: l.qtyMilli, unitCostPaisa: l.line.unit_cost_paisa }),
        0,
      )
  // Off this delivery's bill first, then the supplier's other bills, oldest first.
  const own = (bills ?? []).find((b) => b.bill.goods_receipt_id === chosen)
  const ordered = [...(own ? [own] : []), ...(bills ?? []).filter((b) => b !== own)]
  const shares = spreadPayment(
    total,
    ordered.map((b, i) => ({
      id: b.bill.id,
      owedPaisa: billOwed(b),
      dueDate: own && i === 0 ? '0000-00-00' : (b.bill.due_date ?? b.bill.bill_date),
    })),
  )
  const offOwn = shares.find((s) => s.billId === own?.bill.id)?.amountPaisa ?? 0
  const advance = total - shares.reduce((sum, s) => sum + s.amountPaisa, 0)

  const save = async () => {
    if (!detail) return
    const ok = await write(
      () =>
        buildSupplierCredit(context(), {
          supplierId,
          goodsReceiptId: detail.receipt.id,
          creditNo,
          creditDate: todayIso(),
          reason,
          locationId: detail.receipt.location_id,
          lines: amountOnly
            ? []
            : goods.map((l) => ({
                productId: l.line.product_id,
                qtyMilli: l.qtyMilli,
                unitCostPaisa: l.line.unit_cost_paisa,
                batchId: l.line.batch_id,
              })),
          amountPaisa: amountOnly ? (amount ?? 0) : undefined,
          allocations: shares,
        }).ops,
    )
    if (ok) {
      toast.success(
        amountOnly
          ? `${money(total, settings)} taken off what you owe ${supplierName}`
          : `Returned ${goods.length} item${goods.length === 1 ? '' : 's'} to ${supplierName}`,
      )
      onClose()
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Return goods to ${supplierName}`}
      size="lg"
      footer={
        <>
          <span className="mr-auto self-center text-sm text-muted">
            {total > 0 && own && offOwn > 0
              ? `${own.bill.bill_no || 'Their bill'}: ${money(billOwed(own), settings)} → ${money(billOwed(own) - offOwn, settings)} to pay`
              : ''}
          </span>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={total <= 0 || !detail} onClick={() => void save()}>
            {amountOnly
              ? 'Take it off'
              : `Return ${goods.length || ''} item${goods.length === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      {receipts && receipts.length === 0 ? (
        <EmptyState title="Nothing received from this supplier yet" />
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="From the delivery of">
              {(id) => (
                <Select
                  id={id}
                  value={chosen}
                  onChange={(e) => (setPicked(e.target.value), setBack({}))}
                >
                  {(receipts ?? []).map(({ receipt: g }) => (
                    <option key={g.id} value={g.id}>
                      {dateShort(g.receipt_date, settings)}
                      {g.reference ? ` · bill ${g.reference}` : ''}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Why">
              {(id) => (
                <Select id={id} value={reason} onChange={(e) => setReason(e.target.value)}>
                  {RETURN_REASONS.map((r) => (
                    <option key={r}>{r}</option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Their credit note no. (optional)">
              {(id) => (
                <Input id={id} value={creditNo} onChange={(e) => setCreditNo(e.target.value)} />
              )}
            </Field>
          </div>
          <Switch
            label="No goods are going back"
            description="Only the amount changes, for example a price they corrected."
            checked={amountOnly}
            onChange={(e) => setAmountOnly(e.target.checked)}
          />
          {amountOnly ? (
            <Field label="Amount they took off" className="max-w-xs">
              {(id) => <MoneyInput id={id} paisa={amount} onValue={setAmount} prefix="Rs." />}
            </Field>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border">
              <Table stack>
                <thead>
                  <tr>
                    <TH>Item</TH>
                    <TH align="right">Received</TH>
                    <TH align="right">Sending back</TH>
                    <TH align="right">Amount</TH>
                  </tr>
                </thead>
                <tbody>
                  {lines.map(({ line, product, qtyMilli }) => {
                    const unit = lookups.unit(product?.unit_id)
                    const serials = product?.track_serials ?? false
                    return (
                      <tr key={line.id}>
                        <TD className="stack-main stack-r1 font-medium">
                          {product?.name ?? 'Product'}
                          {serials && (
                            <div className="text-xs font-normal text-muted">
                              Units with serial numbers cannot be returned here yet.
                            </div>
                          )}
                        </TD>
                        <TD align="right" className="stack-end stack-r1 text-muted">
                          {qty(line.qty_milli)} {unit?.symbol}
                        </TD>
                        <TD align="right" className="stack-main stack-r2">
                          <div className="ml-auto w-24 max-sm:ml-0">
                            <QtyInput
                              aria-label={`Sending back ${product?.name ?? 'item'}`}
                              milli={back[line.id] ?? null}
                              allowsDecimal={unit?.allows_decimal ?? true}
                              disabled={serials}
                              onValue={(v) => setBack((b) => ({ ...b, [line.id]: v ?? 0 }))}
                            />
                          </div>
                        </TD>
                        <TD align="right" className="stack-end stack-r2 tabular">
                          {qtyMilli > 0
                            ? money(
                                creditLineAmount({ qtyMilli, unitCostPaisa: line.unit_cost_paisa }),
                                settings,
                              )
                            : '—'}
                        </TD>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          )}
          {total > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              {!amountOnly && (
                <div className="flex items-center gap-3 rounded-xl bg-soft px-3.5 py-2.5">
                  <IconTile area="stock">
                    <Package className="size-[18px]" />
                  </IconTile>
                  <div className="text-sm">
                    <div className="text-xs text-muted">Stock goes down by</div>
                    <div className="font-semibold">
                      {goods
                        .map((l) => `${qty(l.qtyMilli)} × ${l.product?.name ?? 'item'}`)
                        .join(', ')}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 rounded-xl bg-success-light px-3.5 py-2.5 text-success">
                <IconTile area="buy">
                  <Receipt className="size-[18px]" />
                </IconTile>
                <div>
                  <div className="text-xs">
                    {advance >= total ? 'Kept as an advance' : 'Comes off what you owe'}
                  </div>
                  <div className="tabular text-base font-bold">{money(total, settings)}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

// -----------------------------------------------------------------------------
// Cancelling a bill, a payment or a return (managers), with a reason
// -----------------------------------------------------------------------------

function CancelDialog({
  title,
  message,
  busy,
  onClose,
  onConfirm,
}: {
  title: string
  message: string
  busy: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Keep it
          </Button>
          <Button
            variant="danger"
            loading={busy}
            disabled={!reason.trim()}
            onClick={() => onConfirm(reason)}
          >
            Cancel it
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-muted">{message}</p>
      <Field label="Reason">
        {(id) => (
          <Input
            id={id}
            value={reason}
            maxLength={200}
            onChange={(e) => setReason(e.target.value)}
          />
        )}
      </Field>
    </Modal>
  )
}

type Cancelling =
  | { kind: 'bill'; bill: SupplierBillSummary }
  | { kind: 'payment'; payment: SupplierPaymentSummary['payment'] }
  | { kind: 'credit'; credit: SupplierCreditSummary['credit'] }

/** Runs a cancel; returns the dialog to render and the function that opens it. */
function useCancel() {
  const context = useBuildContext()
  const { write, busy } = useWrite()
  const toast = useToast()
  const [target, setTarget] = useState<Cancelling | null>(null)
  const repoMoves = useLive(
    (r) =>
      target?.kind === 'credit'
        ? (r.payables?.creditMovements(target.credit.id) ?? Promise.resolve([]))
        : Promise.resolve([]),
    [target?.kind === 'credit' ? target.credit.id : ''],
    ['stock_movements'],
  ).data
  const confirm = async (reason: string) => {
    if (!target) return
    const ok = await write(() => {
      if (target.kind === 'bill')
        return voidSupplierBill(context(), supplierBillRow(target.bill.bill), reason).ops
      if (target.kind === 'payment')
        return voidSupplierPayment(context(), supplierPaymentRow(target.payment), reason).ops
      return voidSupplierCredit(
        context(),
        supplierCreditRow(target.credit),
        reason,
        (repoMoves ?? []).map(({ created_by: _by, server_updated_at: _at, ...m }) => m),
      ).ops
    })
    if (ok) {
      toast.success('Cancelled')
      setTarget(null)
    }
  }
  const dialog = target && (
    <CancelDialog
      title={
        target.kind === 'bill'
          ? 'Cancel this bill?'
          : target.kind === 'payment'
            ? 'Cancel this payment?'
            : 'Cancel this return?'
      }
      message={
        target.kind === 'bill'
          ? 'Use this for a bill entered by mistake. Cancel its payments and returns first.'
          : target.kind === 'payment'
            ? 'The bills it paid are owed again.'
            : 'The goods come back into stock and the amount is owed again.'
      }
      busy={busy}
      onClose={() => setTarget(null)}
      onConfirm={(reason) => void confirm(reason)}
    />
  )
  return { dialog, cancel: setTarget }
}

// -----------------------------------------------------------------------------
// A delivery's bill (on the purchase's page)
// -----------------------------------------------------------------------------

function Row({
  label,
  value,
  strong,
  tone,
}: {
  label: string
  value: ReactNode
  strong?: boolean
  tone?: string
}) {
  return (
    <div className={cx('flex justify-between gap-3 py-1.5', strong ? 'text-base' : 'text-sm')}>
      <span className="text-muted">{label}</span>
      <span className={cx('tabular', strong ? 'font-bold' : 'font-semibold', tone)}>{value}</span>
    </div>
  )
}

export function ReceiptBillCard({
  receipt,
  supplier,
  totalPaisa,
}: {
  receipt: { id: string; reference: string | null; receipt_date: string }
  supplier: { id: string; name: string } | null
  totalPaisa: number
}) {
  const settings = useSettings()
  const canEdit = useCan('staff')
  const canCancel = useCan('manager')
  const today = todayIso()
  const [dialog, setDialog] = useState<'add' | 'pay' | 'return' | null>(null)
  const cancelling = useCancel()
  const detail = useLive(
    async (r) => {
      const bills = (await r.payables?.bills({ receiptIds: [receipt.id] })) ?? []
      return bills[0] ? ((await r.payables?.billDetail(bills[0].bill.id)) ?? null) : null
    },
    [receipt.id],
    PAYABLE_TABLES,
  )
  if (!supplier)
    return (
      <Card>
        <h2 className="font-semibold">Supplier’s bill</h2>
        <p className="mt-1 text-sm text-muted">
          This delivery has no supplier, so there is no bill to track.
        </p>
      </Card>
    )
  const d = detail.data
  const dialogs = (
    <>
      {dialog === 'add' && (
        <AddBillDialog
          supplierId={supplier.id}
          supplierName={supplier.name}
          receipt={{
            id: receipt.id,
            reference: receipt.reference,
            totalPaisa,
            date: receipt.receipt_date,
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'pay' && (
        <PaySupplierDialog
          supplierId={supplier.id}
          supplierName={supplier.name}
          billId={d?.bill.id}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'return' && (
        <ReturnGoodsDialog
          supplierId={supplier.id}
          supplierName={supplier.name}
          receiptId={receipt.id}
          onClose={() => setDialog(null)}
        />
      )}
      {cancelling.dialog}
    </>
  )
  if (!d)
    return (
      <Card>
        <h2 className="font-semibold">Supplier’s bill</h2>
        <p className="mt-1 text-sm text-muted">
          {detail.loading ? 'Loading…' : 'No bill is recorded for this delivery.'}
        </p>
        {canEdit && !detail.loading && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button icon={<Plus className="size-4" />} onClick={() => setDialog('add')}>
              Add the supplier’s bill
            </Button>
            <Button
              variant="secondary"
              icon={<Undo2 className="size-4" />}
              onClick={() => setDialog('return')}
            >
              Return goods
            </Button>
          </div>
        )}
        {dialogs}
      </Card>
    )
  const status = billStatus(d, today)
  const paid = d.payments
    .filter((p) => p.payment.status === 'active')
    .reduce((sum, p) => sum + p.amountPaisa, 0)
  const credited = d.credits
    .filter((c) => c.credit.status === 'active')
    .reduce((sum, c) => sum + c.amountPaisa, 0)
  return (
    <Card aria-label="Supplier’s bill" role="region">
      <div className="flex items-center gap-2.5">
        <IconTile area="buy">
          <Receipt className="size-[18px]" />
        </IconTile>
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-semibold">
            {d.bill.bill_no ? `Bill ${d.bill.bill_no}` : 'Supplier’s bill'}
          </h2>
          <p className="text-xs text-muted">
            {dateShort(d.bill.bill_date, settings)}
            {d.bill.due_date &&
              ` · due ${dateShort(d.bill.due_date, settings)}${dueIn(d.bill, today) ? ` (${dueIn(d.bill, today)})` : ''}`}
          </p>
        </div>
        <Badge tone={status.tone}>{status.label}</Badge>
      </div>
      <div className="mt-3">
        <Row label="Bill total" value={money(d.bill.total_paisa, settings)} />
        {paid > 0 && <Row label="Paid" value={`−${money(paid, settings)}`} />}
        {credited > 0 && <Row label="Returned" value={`−${money(credited, settings)}`} />}
        <div className="mt-1 border-t border-border pt-1">
          <Row
            label="Still to pay"
            value={money(status.owedPaisa, settings)}
            strong
            tone={status.owedPaisa > 0 ? 'text-warning' : 'text-success'}
          />
        </div>
      </div>
      {canEdit && (
        <div className="mt-3 flex flex-wrap gap-2">
          {status.owedPaisa > 0 && (
            <Button icon={<HandCoins className="size-4" />} onClick={() => setDialog('pay')}>
              Pay
            </Button>
          )}
          <Button
            variant="secondary"
            icon={<Undo2 className="size-4" />}
            onClick={() => setDialog('return')}
          >
            Return goods
          </Button>
          {canCancel && paid + credited === 0 && (
            <Button
              variant="ghost"
              className="ml-auto"
              onClick={() => cancelling.cancel({ kind: 'bill', bill: d })}
            >
              Cancel bill
            </Button>
          )}
        </div>
      )}
      {(d.payments.length > 0 || d.credits.length > 0) && (
        <>
          <h3 className="mt-4 text-section text-subtle">Against this bill</h3>
          <ul className="mt-1 divide-y divide-soft">
            {d.payments.map(({ payment: p, amountPaisa }) => (
              <li
                key={p.id}
                className={cx(
                  'flex items-center gap-2.5 py-2',
                  p.status === 'void' && 'opacity-60',
                )}
              >
                <IconTile area="buy" size="sm">
                  <HandCoins className="size-4" />
                </IconTile>
                <span className="min-w-0 flex-1 text-sm">
                  <span className="block font-medium">
                    Paid by {payMethodLabel(p.method).toLowerCase()}
                    {p.status === 'void' && ' (cancelled)'}
                  </span>
                  <span className="block text-xs text-muted">
                    {dateShort(p.payment_date, settings)}
                    {p.reference && ` · ${p.reference}`}
                  </span>
                </span>
                <span className="tabular text-sm font-semibold">
                  {money(amountPaisa, settings)}
                </span>
              </li>
            ))}
            {d.credits.map(({ credit: c, amountPaisa }) => (
              <li
                key={c.id}
                className={cx(
                  'flex items-center gap-2.5 py-2',
                  c.status === 'void' && 'opacity-60',
                )}
              >
                <IconTile area="stock" size="sm">
                  <Undo2 className="size-4" />
                </IconTile>
                <span className="min-w-0 flex-1 text-sm">
                  <span className="block font-medium">
                    Returned{c.reason ? `: ${c.reason.toLowerCase()}` : ''}
                    {c.status === 'void' && ' (cancelled)'}
                  </span>
                  <span className="block text-xs text-muted">
                    {dateShort(c.credit_date, settings)}
                    {c.credit_no && ` · credit note ${c.credit_no}`}
                  </span>
                </span>
                <span className="tabular text-sm font-semibold">
                  {money(amountPaisa, settings)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {dialogs}
    </Card>
  )
}

// -----------------------------------------------------------------------------
// A supplier's money: what is owed, bill by bill, and what was paid or returned
// -----------------------------------------------------------------------------

type SupplierDialog = { kind: 'pay'; billId?: string } | { kind: 'add' } | { kind: 'return' } | null

/** The pay / add a bill / return dialogs of a supplier's page, opened from its header too. */
export function useSupplierDialogs() {
  const [dialog, setDialog] = useState<SupplierDialog>(null)
  return { dialog, setDialog }
}

/** The page header's solid action. */
export function SupplierPayButton({ dialogs }: { dialogs: ReturnType<typeof useSupplierDialogs> }) {
  return (
    <Button
      icon={<HandCoins className="size-4" />}
      onClick={() => dialogs.setDialog({ kind: 'pay' })}
    >
      Pay supplier
    </Button>
  )
}

/** Its quieter neighbours (behind "More" on a phone). */
export function SupplierMoreButtons({
  dialogs,
}: {
  dialogs: ReturnType<typeof useSupplierDialogs>
}) {
  return (
    <>
      <Button
        variant="secondary"
        icon={<Plus className="size-4" />}
        onClick={() => dialogs.setDialog({ kind: 'add' })}
      >
        Add a bill
      </Button>
      <Button
        variant="secondary"
        icon={<Undo2 className="size-4" />}
        onClick={() => dialogs.setDialog({ kind: 'return' })}
      >
        Return goods
      </Button>
    </>
  )
}

export interface SupplierTab {
  id: string
  label: string
  count?: number
  content: ReactNode
}

export function SupplierMoney({
  supplierId,
  supplierName,
  dialogs,
  stat,
  tabs = [],
}: {
  supplierId: string
  supplierName: string
  dialogs: ReturnType<typeof useSupplierDialogs>
  /** A fourth figure, from the page (what was bought, the last delivery). */
  stat?: ReactNode
  /** The page's own lists, as tabs after the two money ones. */
  tabs?: SupplierTab[]
}) {
  const settings = useSettings()
  const canEdit = useCan('staff')
  const canCancel = useCan('manager')
  const today = todayIso()
  const [tab, setTab] = useState('bills')
  const { dialog, setDialog } = dialogs
  const cancelling = useCancel()
  const data = useLive(
    async (r) => {
      if (!r.payables) return null
      const [bills, payments, credits] = await Promise.all([
        r.payables.bills({ supplierId }),
        r.payables.payments({ supplierId }),
        r.payables.credits({ supplierId }),
      ])
      return { bills, payments, credits }
    },
    [supplierId],
    PAYABLE_TABLES,
  ).data
  const extra = tabs.find((t) => t.id === tab)
  if (!data) return null
  const open = data.bills.filter((b) => billOwed(b) > 0)
  const owed = open.reduce((sum, b) => sum + billOwed(b), 0)
  const late = open.filter((b) => billStatus(b, today).lateDays > 0)
  const lateOwed = late.reduce((sum, b) => sum + billOwed(b), 0)
  const given =
    data.payments
      .filter((p) => p.payment.status === 'active')
      .reduce((s, p) => s + p.payment.amount_paisa - p.allocatedPaisa, 0) +
    data.credits
      .filter((c) => c.credit.status === 'active')
      .reduce((s, c) => s + c.credit.total_paisa - c.allocatedPaisa, 0)
  const month = today.slice(0, 7)
  const paidThisMonth = data.payments
    .filter((p) => p.payment.status === 'active' && p.payment.payment_date.startsWith(month))
    .reduce((s, p) => s + p.payment.amount_paisa, 0)
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <Stat
          area="buy"
          label="You owe"
          value={money(owed, settings)}
          hint={
            open.length === 0
              ? 'Nothing unpaid'
              : `${open.length} unpaid bill${open.length === 1 ? '' : 's'}`
          }
          icon={<HandCoins className="size-5" />}
        />
        {given > 0 ? (
          <Stat
            area="insight"
            label="Advance with them"
            value={money(given, settings)}
            hint="Comes off their next bill"
            icon={<Receipt className="size-5" />}
          />
        ) : (
          <Stat
            area={lateOwed > 0 ? 'people' : undefined}
            label="Late"
            value={
              <span className={cx(lateOwed > 0 && 'text-danger')}>{money(lateOwed, settings)}</span>
            }
            hint={
              late.length === 0
                ? 'Nothing past its due date'
                : `${late.length} bill${late.length === 1 ? '' : 's'} past due`
            }
            icon={<AlertTriangle className="size-5" />}
          />
        )}
        <Stat
          area="sell"
          label="Paid this month"
          value={money(paidThisMonth, settings)}
          icon={<Receipt className="size-5" />}
        />
        {stat}
      </div>
      <Card className="p-0" role="region" aria-label="What you owe this supplier">
        <div
          role="tablist"
          aria-label="Bills, payments and purchases"
          className="flex gap-1 overflow-x-auto border-b border-border px-2"
        >
          {[
            { id: 'bills', label: 'Unpaid bills', count: open.length },
            { id: 'history', label: 'Payments and returns', count: 0 },
            ...tabs.map((t) => ({ id: t.id, label: t.label, count: t.count ?? 0 })),
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={cx(
                '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-3 text-sm whitespace-nowrap',
                tab === t.id
                  ? 'border-buy font-bold text-buy'
                  : 'border-transparent font-semibold text-muted hover:text-ink',
              )}
            >
              {t.label}
              {t.count > 0 && <span className="tabular text-xs">{t.count}</span>}
            </button>
          ))}
        </div>
        {extra ? (
          extra.content
        ) : tab === 'bills' ? (
          open.length === 0 ? (
            <EmptyState
              icon={<HandCoins className="size-6" />}
              title="Nothing is owed to this supplier"
            />
          ) : (
            <Table stack>
              <thead>
                <tr>
                  <TH>Bill</TH>
                  <TH>Date</TH>
                  <TH>Due</TH>
                  <TH align="right">Total</TH>
                  <TH align="right">Paid or returned</TH>
                  <TH align="right">To pay</TH>
                  <TH />
                </tr>
              </thead>
              <tbody>
                {open.map((b) => {
                  const status = billStatus(b, today)
                  const name = b.bill.bill_no || b.bill.notes || 'Bill'
                  return (
                    <TR key={b.bill.id} className={cx(status.lateDays > 0 && 'bg-danger-light/40')}>
                      <TD className="stack-main stack-r1 font-medium">
                        {b.bill.goods_receipt_id ? (
                          <Link
                            to={`/purchases/${b.bill.goods_receipt_id}`}
                            className="font-semibold text-primary hover:underline"
                          >
                            {name}
                          </Link>
                        ) : (
                          <>
                            {name} <Badge>No delivery</Badge>
                          </>
                        )}
                      </TD>
                      <TD
                        className="stack-r2 text-muted max-sm:text-xs"
                        title={dateLabel(b.bill.bill_date, settings)}
                      >
                        {dateShort(b.bill.bill_date, settings)}
                      </TD>
                      <TD className="stack-r3 max-sm:text-xs">
                        {b.bill.due_date ? (
                          dateShort(b.bill.due_date, settings)
                        ) : (
                          <span className="text-muted">—</span>
                        )}{' '}
                        {status.lateDays > 0 ? (
                          <Badge tone="danger">{status.label}</Badge>
                        ) : (
                          <span className="text-muted">{dueIn(b.bill, today)}</span>
                        )}
                      </TD>
                      <TD align="right" className="max-sm:hidden">
                        {money(b.bill.total_paisa, settings)}
                      </TD>
                      <TD align="right" className="text-muted max-sm:hidden">
                        {money(b.coveredPaisa, settings)}
                      </TD>
                      <TD align="right" className="stack-end stack-r1 tabular font-semibold">
                        {money(status.owedPaisa, settings)}
                      </TD>
                      <TD align="right" className="stack-end stack-r2">
                        <span className="flex justify-end gap-1.5">
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="soft"
                              aria-label={`Pay ${name}`}
                              onClick={() => setDialog({ kind: 'pay', billId: b.bill.id })}
                            >
                              Pay
                            </Button>
                          )}
                          {canCancel && !b.bill.goods_receipt_id && b.coveredPaisa === 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`Cancel ${name}`}
                              onClick={() => cancelling.cancel({ kind: 'bill', bill: b })}
                            >
                              Cancel
                            </Button>
                          )}
                        </span>
                      </TD>
                    </TR>
                  )
                })}
              </tbody>
            </Table>
          )
        ) : data.payments.length + data.credits.length === 0 ? (
          <EmptyState title="No payments or returns yet" />
        ) : (
          <ul className="divide-y divide-soft">
            {[
              ...data.payments.map((p) => ({
                at: p.payment.payment_date,
                key: p.payment.id,
                p,
                c: null as SupplierCreditSummary | null,
              })),
              ...data.credits.map((c) => ({
                at: c.credit.credit_date,
                key: c.credit.id,
                p: null as SupplierPaymentSummary | null,
                c,
              })),
            ]
              .sort((a, b) => b.at.localeCompare(a.at))
              .map(({ key, p, c }) => {
                const voided = (p?.payment.status ?? c?.credit.status) === 'void'
                return (
                  <li
                    key={key}
                    className={cx('flex items-center gap-3 px-4 py-2.5', voided && 'opacity-60')}
                  >
                    <IconTile area={p ? 'buy' : 'stock'} size="sm">
                      {p ? <HandCoins className="size-4" /> : <Undo2 className="size-4" />}
                    </IconTile>
                    <span className="min-w-0 flex-1 text-sm">
                      <span className="block font-medium">
                        {p
                          ? `Paid by ${payMethodLabel(p.payment.method).toLowerCase()}`
                          : c!.lineCount > 0
                            ? `Returned goods${c!.credit.reason ? `: ${c!.credit.reason.toLowerCase()}` : ''}`
                            : `Amount taken off${c!.credit.reason ? `: ${c!.credit.reason.toLowerCase()}` : ''}`}
                        {voided && ' (cancelled)'}
                      </span>
                      <span className="block truncate text-xs text-muted">
                        {dateShort(p ? p.payment.payment_date : c!.credit.credit_date, settings)}
                        {p?.payment.reference && ` · ${p.payment.reference}`}
                        {c?.credit.credit_no && ` · credit note ${c.credit.credit_no}`}
                        {p && p.bills.length > 0 && ` · for ${p.bills.join(', ')}`}
                        {p &&
                          p.payment.status === 'active' &&
                          p.allocatedPaisa < p.payment.amount_paisa &&
                          ` · ${money(p.payment.amount_paisa - p.allocatedPaisa, settings)} as advance`}
                      </span>
                    </span>
                    <span className="tabular text-sm font-semibold">
                      {money(p ? p.payment.amount_paisa : c!.credit.total_paisa, settings)}
                    </span>
                    {canCancel && !voided && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          cancelling.cancel(
                            p
                              ? { kind: 'payment', payment: p.payment }
                              : { kind: 'credit', credit: c!.credit },
                          )
                        }
                      >
                        Cancel
                      </Button>
                    )}
                  </li>
                )
              })}
          </ul>
        )}
      </Card>
      {dialog?.kind === 'pay' && (
        <PaySupplierDialog
          supplierId={supplierId}
          supplierName={supplierName}
          billId={dialog.billId}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'add' && (
        <AddBillDialog
          supplierId={supplierId}
          supplierName={supplierName}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'return' && (
        <ReturnGoodsDialog
          supplierId={supplierId}
          supplierName={supplierName}
          onClose={() => setDialog(null)}
        />
      )}
      {cancelling.dialog}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Payments made (Purchase → Payments made)
// -----------------------------------------------------------------------------

/** The row the arrow keys are on. */
const ROW_ACTIVE = 'sm:bg-buy-wash sm:shadow-[inset_3px_0_0_var(--color-buy)]'

export function PaymentsMadeScreen() {
  const settings = useSettings()
  const canCancel = useCan('manager')
  const [search, setSearch] = useState('')
  const [method, setMethod] = useState<PaymentMethod | null>(null)
  const canPay = useCan('staff')
  const [picking, setPicking] = useState(false)
  const [paying, setPaying] = useState<{ id: string; name: string } | null>(null)
  const cancelling = useCancel()
  const payments = useLive(
    (r) => r.payables?.payments() ?? Promise.resolve([]),
    [],
    [...PAYABLE_TABLES, 'suppliers'],
  ).data
  const needle = search.trim().toLowerCase()
  const shown = (payments ?? []).filter(
    (p) =>
      (!method || p.payment.method === method) &&
      (!needle ||
        (p.supplierName ?? '').toLowerCase().includes(needle) ||
        (p.payment.reference ?? '').toLowerCase().includes(needle)),
  )
  const month = todayIso().slice(0, 7)
  const thisMonth = (payments ?? []).filter(
    (p) => p.payment.status === 'active' && p.payment.payment_date.startsWith(month),
  )
  const total = shown
    .filter((p) => p.payment.status === 'active')
    .reduce((sum, p) => sum + p.payment.amount_paisa, 0)
  // 25 a page; the total under the list is of every payment shown, not just this page.
  const scope = JSON.stringify([search, method])
  const paged = usePaged(shown, scope)
  const used = new Set((payments ?? []).map((p) => p.payment.method))
  const openItem = useOpen()
  // A payment made on another device slides into the list it reaches.
  const arrived = useArrivals(
    paged.rows.map((p) => p.payment.id),
    payments !== undefined,
    `${scope}:${paged.offset}`,
  )
  // The keys: the arrows move a selected row, Enter opens its supplier.
  const [active, setActive] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const activeRow = paged.rows.find((p) => p.payment.id === active)
  useListKeys({
    ids: paged.rows.map((p) => p.payment.id),
    active,
    setActive,
    page: paged,
    list: listRef,
    open: () => activeRow && openItem(`/suppliers/${activeRow.payment.supplier_id}`),
    keys: {},
  })
  return (
    <div>
      <PageHeader
        title="Bills and payments"
        subtitle={
          payments && (
            <>
              <b className="font-semibold text-ink">
                {money(
                  thisMonth.reduce((sum, p) => sum + p.payment.amount_paisa, 0),
                  settings,
                )}{' '}
                paid
              </b>{' '}
              to suppliers this month
            </>
          )
        }
        actions={
          canPay && (
            <Button icon={<HandCoins className="size-4" />} onClick={() => setPicking(true)}>
              Pay a supplier
            </Button>
          )
        }
      />
      <SectionTabs section="bills" />
      <ListToolbar>
        <FilterPills label="How it was paid">
          <FilterPill selected={method === null} onSelect={() => setMethod(null)}>
            All
          </FilterPill>
          {PAY_METHODS.filter((m) => used.has(m.value)).map((m) => (
            <FilterPill
              key={m.value}
              selected={method === m.value}
              onSelect={() => setMethod(m.value)}
            >
              {m.label}
            </FilterPill>
          ))}
        </FilterPills>
        <SearchInput
          listKeys
          className="max-sm:w-full sm:ml-auto sm:w-64"
          value={search}
          onChange={setSearch}
          placeholder="Supplier or reference…"
        />
      </ListToolbar>
      <ListFrame ref={listRef}>
        {!payments ? (
          <SkeletonTable cols={5} />
        ) : shown.length === 0 ? (
          <EmptyState
            icon={<HandCoins className="size-6" />}
            title={payments.length === 0 ? 'No payments to suppliers yet' : 'No payments match'}
            description={
              payments.length === 0
                ? 'When you pay a supplier, from their page or from a purchase, it is listed here.'
                : 'Try another way of paying or search.'
            }
          />
        ) : (
          <>
            <Table stack="cards">
              <thead>
                <tr>
                  <TH>Payment</TH>
                  <TH>Date</TH>
                  <TH>What it paid</TH>
                  <TH>Status</TH>
                  <TH align="right">Amount</TH>
                  <TH />
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((p) => {
                  const voided = p.payment.status === 'void'
                  const advance = p.payment.amount_paisa - p.allocatedPaisa
                  const supplier = p.supplierName ?? 'Supplier'
                  const to = `/suppliers/${p.payment.supplier_id}`
                  const how = [payMethodLabel(p.payment.method), p.payment.reference]
                    .filter(Boolean)
                    .join(' · ')
                  return (
                    <TR
                      key={p.payment.id}
                      onClick={() => openItem(to)}
                      aria-current={active === p.payment.id ? 'true' : undefined}
                      className={cx(
                        voided && 'text-muted line-through',
                        arrived.has(p.payment.id) && arrivalClass,
                        active === p.payment.id && ROW_ACTIVE,
                      )}
                    >
                      <TD className="stack-title">
                        <RowTitle
                          tile={
                            <IconTile area="buy" size="sm">
                              <HandCoins className="size-4" />
                            </IconTile>
                          }
                          title={
                            <Link
                              to={to}
                              className="hover:underline focus-visible:underline"
                              onClick={(e) => {
                                e.stopPropagation()
                                moveClick(to, 'open')(e)
                              }}
                            >
                              {supplier}
                            </Link>
                          }
                          sub={how}
                        />
                      </TD>
                      <TD
                        className="stack-r3 whitespace-nowrap text-muted max-sm:text-xs"
                        title={dateLabel(p.payment.payment_date, settings)}
                      >
                        {dateShort(p.payment.payment_date, settings)}
                      </TD>
                      <TD className="stack-r3 max-sm:hidden">
                        <RowNote
                          main={
                            p.bills.length > 0
                              ? `For ${p.bills.join(', ')}`
                              : advance > 0
                                ? 'Paid in advance'
                                : '—'
                          }
                          sub={
                            !voided && advance > 0 && p.bills.length > 0
                              ? `${money(advance, settings)} kept as advance`
                              : undefined
                          }
                        />
                      </TD>
                      <TD className="stack-end stack-r2">
                        {voided ? (
                          <Badge>Cancelled</Badge>
                        ) : advance > 0 ? (
                          <Badge tone="info">Advance</Badge>
                        ) : (
                          <Badge tone="success">Paid</Badge>
                        )}
                      </TD>
                      <TD align="right" className="stack-end stack-r1 whitespace-nowrap font-bold">
                        {money(p.payment.amount_paisa, settings)}
                      </TD>
                      <TD className="stack-actions sm:w-px sm:whitespace-nowrap">
                        <RowActions>
                          {canCancel && !voided && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-[34px] text-[13px]"
                              aria-label={`Cancel the payment to ${supplier}`}
                              onClick={() =>
                                cancelling.cancel({ kind: 'payment', payment: p.payment })
                              }
                            >
                              Cancel
                            </Button>
                          )}
                        </RowActions>
                      </TD>
                    </TR>
                  )
                })}
              </tbody>
            </Table>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
              <span>
                <b className="font-semibold text-ink">{money(total, settings)}</b> paid in this list
                · Newest first. A row opens the supplier.
              </span>
              <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
                <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Enter</Kbd> open
              </span>
            </div>
            <Pagination
              offset={paged.offset}
              limit={paged.limit}
              total={paged.total}
              onChange={paged.setOffset}
            />
          </>
        )}
      </ListFrame>
      {picking && (
        <PickSupplierDialog
          onClose={() => setPicking(false)}
          onPick={(supplier) => {
            setPicking(false)
            setPaying(supplier)
          }}
        />
      )}
      {paying && (
        <PaySupplierDialog
          supplierId={paying.id}
          supplierName={paying.name}
          onClose={() => setPaying(null)}
        />
      )}
      {cancelling.dialog}
    </div>
  )
}

/** Which supplier to pay: the ones owed something first, with what they are owed. */
function PickSupplierDialog({
  onClose,
  onPick,
}: {
  onClose: () => void
  onPick: (supplier: { id: string; name: string }) => void
}) {
  const settings = useSettings()
  const data = useLive(
    async (r) => {
      const [suppliers, owed] = await Promise.all([
        r.list('suppliers', {
          where: { is_active: true },
          orderBy: { column: 'name' },
          limit: 500,
        }),
        r.payables?.owed() ?? Promise.resolve([]),
      ])
      return { suppliers: suppliers.rows, owed: new Map(owed.map((o) => [o.supplierId, o])) }
    },
    [],
    [...PAYABLE_TABLES, 'suppliers'],
  ).data
  const sorted = [...(data?.suppliers ?? [])].sort(
    (a, b) =>
      (data!.owed.get(b.id)?.owedPaisa ?? 0) - (data!.owed.get(a.id)?.owedPaisa ?? 0) ||
      a.name.localeCompare(b.name),
  )
  const canAdd = useCan('staff')
  const [adding, setAdding] = useState(false)
  const addButton = (first: boolean) => (
    <Button
      variant={first ? 'primary' : 'ghost'}
      size="sm"
      icon={<Plus className="size-4" aria-hidden />}
      onClick={() => setAdding(true)}
    >
      {first ? 'Add your first supplier' : 'New supplier'}
    </Button>
  )
  if (adding)
    return (
      <NewPartyDialog
        kind="suppliers"
        initialName=""
        onClose={() => setAdding(false)}
        onCreated={(supplier) => onPick({ id: supplier.id, name: supplier.name })}
      />
    )
  return (
    <Modal open onClose={onClose} title="Pay a supplier" size="sm" unsaved={false}>
      {!data ? (
        <p className="text-sm text-muted">Loading suppliers…</p>
      ) : sorted.length === 0 ? (
        <EmptyState
          title="No suppliers yet"
          description={canAdd ? undefined : 'Ask a manager to add one under Suppliers.'}
          action={canAdd ? addButton(true) : undefined}
        />
      ) : (
        <>
          <ul className="-mx-2 max-h-80 overflow-y-auto">
            {sorted.map((x) => {
              const owed = data.owed.get(x.id)?.owedPaisa ?? 0
              return (
                <li key={x.id}>
                  <button
                    type="button"
                    onClick={() => onPick({ id: x.id, name: x.name })}
                    className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-2 text-left text-sm hover:bg-soft"
                  >
                    <span className="min-w-0 truncate font-semibold text-primary">{x.name}</span>
                    <span
                      className={cx('tabular shrink-0', owed > 0 ? 'font-bold' : 'text-subtle')}
                    >
                      {owed > 0 ? money(owed, settings) : 'Nothing owed'}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
          {canAdd && <div className="mt-2 border-t border-border pt-2">{addButton(false)}</div>}
        </>
      )}
    </Modal>
  )
}
